import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateCard from '../components/dashboard/CreateCard';
import ApproveCard from '../components/dashboard/ApproveCard';
import QuickStatsSection from '../components/dashboard/QuickStatsSection';
import UpcomingPostsSection from '../components/dashboard/UpcomingPostsSection';
import Card from '../components/Card';
import Button from '../components/Button';
import apiClient from '../lib/api';

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [onboardingComplete, setOnboardingComplete] = useState<boolean>(true);
  const [isLoadingOnboarding, setIsLoadingOnboarding] = useState<boolean>(true);

  // Check if onboarding is complete on mount
  useEffect(() => {
    async function checkOnboarding() {
      try {
        await apiClient.get('/auth/me');

        // Check if workspace has completed onboarding (you may need to adjust this based on your API)
        // For now, we assume onboarding is complete if workspace exists
        // In future, add onboardingCompleted field to workspace
        setOnboardingComplete(true);
      } catch (err) {
        console.error('Failed to check onboarding status:', err);
        setOnboardingComplete(true); // Default to complete on error
      } finally {
        setIsLoadingOnboarding(false);
      }
    }

    checkOnboarding();
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Onboarding Resume Card (conditionally shown) */}
      {!isLoadingOnboarding && !onboardingComplete && (
        <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-400 dark:border-yellow-600">
          <div className="flex items-start gap-4">
            <div className="text-4xl">⚠️</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">
                {t('dashboard.onboarding.incompleteTitle', 'Onboarding not completed')}
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                {t('dashboard.onboarding.incompleteMessage', 'Complete your workspace setup to unlock all features and start creating content.')}
              </p>
              <Button onClick={() => navigate('/onboarding')}>
                {t('dashboard.onboarding.resumeButton', 'Resume onboarding')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Row 1: Create + Approve Cards (50/50) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CreateCard />
        <ApproveCard />
      </div>

      {/* Row 2: Quick Stats + AI Assistant placeholder */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <QuickStatsSection />

        {/* AI Assistant Placeholder */}
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-8 flex items-center justify-center bg-white dark:bg-gray-800">
          <div className="text-center">
            <div className="text-4xl mb-2">🤖</div>
            <p className="text-gray-500 dark:text-gray-400 font-medium">
              {t('dashboard.ai.comingSoon', 'AI Assistant (coming soon)')}
            </p>
          </div>
        </div>
      </div>

      {/* Row 3: Upcoming Posts (full width) */}
      <UpcomingPostsSection />
    </div>
  );
}
